/**
 * The bytes of the pictures and faces a file draws, kept in the browser between visits.
 *
 * A picture on Flobi's storage arrives either from `storage.flobi.ai` or, when it is private,
 * through a link the media service signs afresh on every open. A fresh signature is a new
 * URL, so the browser's own cache never matched it and every open downloaded every picture
 * again. Here a copy is kept by where it is stored (the link without its signature), in the
 * Cache API, so the second open draws from the disk. Access is still checked: the signed
 * link is still asked for each time; only its download is spared.
 *
 * A copy is kept for `MAX_AGE_MS`, at most `MAX_ENTRIES` of them (the oldest go first), none
 * larger than `MAX_BYTES`. Nothing is kept from anywhere but Flobi's storage, and signing out
 * forgets everything (`clearMediaCache`). Where the Cache API is missing or refuses (a
 * private window, an insecure origin, a full disk) this is a plain `fetch`.
 */

const CACHE_NAME = 'flobi-artwork-media-v1';
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX_ENTRIES = 600;
export const MAX_BYTES = 80 * 1024 * 1024;
const STAMP = 'x-artwork-kept-at';

/** Where a copy of `url` is kept, or null when it is not one to keep. */
export function mediaCacheKey(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  const host = parsed.hostname;
  if (host !== 'storage.flobi.ai' && !host.endsWith('.r2.cloudflarestorage.com')) return null;
  // The signature changes on every open; the object it points at does not.
  for (const name of [...parsed.searchParams.keys()]) {
    if (/^x-amz-/i.test(name)) parsed.searchParams.delete(name);
  }
  return parsed.toString();
}

let opened: Promise<Cache | null> | null = null;

function openCache(): Promise<Cache | null> {
  opened ??= (async () => {
    try {
      if (typeof caches === 'undefined') return null;
      return await caches.open(CACHE_NAME);
    } catch {
      return null;
    }
  })();
  return opened;
}

/** `fetch`, answered from the kept copy when there is a fresh one. */
export async function cachedFetch(url: string, init?: RequestInit): Promise<Response> {
  const key = mediaCacheKey(url);
  const cache = key ? await openCache() : null;
  if (!key || !cache) return fetch(url, init);
  try {
    const hit = await cache.match(key);
    if (hit) {
      const keptAt = Number(hit.headers.get(STAMP));
      if (keptAt && Date.now() - keptAt < MAX_AGE_MS) return hit;
      void cache.delete(key).catch(() => undefined);
    }
  } catch {
    // Read from the network as if nothing were kept.
  }
  const response = await fetch(url, init);
  if (response.status === 200 && !(Number(response.headers.get('content-length')) > MAX_BYTES)) {
    void keep(cache, key, response.clone());
  }
  return response;
}

async function keep(cache: Cache, key: string, response: Response): Promise<void> {
  try {
    const body = await response.blob();
    if (body.size > MAX_BYTES) return;
    const headers = new Headers({ [STAMP]: String(Date.now()) });
    const type = response.headers.get('content-type');
    if (type) headers.set('content-type', type);
    await cache.put(key, new Response(body, { status: 200, headers }));
    scheduleTrim(cache, MAX_ENTRIES);
  } catch (error) {
    // A full disk: make room for next time.
    if ((error as { name?: string }).name === 'QuotaExceededError') scheduleTrim(cache, Math.floor(MAX_ENTRIES / 4));
  }
}

let trimming: ReturnType<typeof setTimeout> | null = null;
let trimTo = MAX_ENTRIES;

function scheduleTrim(cache: Cache, limit: number): void {
  trimTo = Math.min(trimTo, limit);
  if (trimming) return;
  trimming = setTimeout(() => {
    trimming = null;
    const target = trimTo;
    trimTo = MAX_ENTRIES;
    void trimCache(cache, target);
  }, 2_000);
}

/** Forgets the oldest copies until at most `limit` are left (keys are in the order kept). */
export async function trimCache(cache: Cache, limit: number): Promise<void> {
  try {
    const keys = await cache.keys();
    for (const request of keys.slice(0, Math.max(0, keys.length - limit))) await cache.delete(request);
  } catch {
    // Tried again after the next copy is kept.
  }
}

/** Forgets every kept copy: on signing out, so the next person on this browser sees none. */
export async function clearMediaCache(): Promise<void> {
  opened = null;
  try {
    if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME);
  } catch {
    // Nothing kept, or nothing reachable.
  }
}
